import { useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/daisy';
import useExamWorkspace from '@/data/hook/exam/useExamWorkspace';
import useExams from '@/data/hook/exam/useExams';
import CoursePath from '@/models/CoursePath';
import Exam from '@/models/Exam';

import ExamDefaultTimeField from './ExamDefaultTimeField';
import ExamSettingsAreasSection from './ExamSettingsAreasSection';

type DefaultTimeFields = { defaultTime: number };

type ExamSettingsTabProps = {
  exam: Exam;
  coursePath: CoursePath;
};

export default function ExamSettingsTab({
  exam,
  coursePath,
}: ExamSettingsTabProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.settings',
  });
  const subcategory = coursePath.course?.module?.courseLine?.subcategory;

  const { cache } = useExamWorkspace();
  const { updateExam, isUpdating } = useExams({
    coursePath,
  });

  const defaultValues = useMemo(
    () => ({ defaultTime: exam.defaultTime }),
    [exam.defaultTime],
  );
  const { control, handleSubmit } = useForm<DefaultTimeFields>({
    defaultValues,
  });
  const { field, fieldState } = useController({
    control,
    name: 'defaultTime',
    rules: {
      required: t('defaultTimeRequired'),
      min: { value: 1, message: t('defaultTimeMin') },
    },
  });

  const hasChanges = field.value !== exam.defaultTime;

  const handleSave = async (data: DefaultTimeFields) => {
    const updated = await updateExam({ examId: exam.id, data });
    cache?.merge(updated);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleSave)}>
      <ExamDefaultTimeField
        value={field.value}
        onChange={field.onChange}
        error={fieldState.error?.message}
      />
      <ExamSettingsAreasSection examId={exam.id} subcategory={subcategory} />
      <div className="flex justify-end">
        <Button
          type="submit"
          color="primary"
          className="px-16"
          disabled={!hasChanges}
          loading={isUpdating}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
